import { Popper, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ReactQuill from 'react-quill';
import Quill from 'quill';
import React, { useEffect, useRef, useState } from 'react';
import Grid2Icon from 'ui/elements/icons/Grid-2-Icon';
import IconButton from 'ui/elements/icons/IconButton';
import ImageIcon from 'ui/elements/icons/ImageIcon';
import TrashIcon from 'ui/elements/icons/TrashIcon';
import Tooltip from 'ui/elements/Tooltip';
import Card from 'ui/views/cards/Card';

interface ImageOptionsMenuProps {
  imgElement: HTMLElement;
  quillRoot: HTMLElement | null;
  quillRef: React.RefObject<ReactQuill>;
  isFirstChild: boolean;
  onClose: () => void;
}

function isEmptyParagraph(p: HTMLElement): boolean {
  return p.children.length === 0 || (p.children.length === 1 && p.children[0].tagName === 'BR');
}

const ImageOptionsMenu: React.FC<ImageOptionsMenuProps> = ({
  imgElement,
  quillRoot,
  quillRef,
  isFirstChild,
  onClose,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(imgElement);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        quillRoot &&
        !quillRoot.contains(target) // Don't close if clicking inside Quill
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, imgElement]);

  function getGridType() {
    const parent = imgElement.closest('p');
    if (!parent) return 'single';
    return parent.classList.contains('ql-grid-3')
      ? 'grid-3'
      : parent.classList.contains('ql-grid-2')
        ? 'grid-2'
        : 'single';
  }

  const [gridType, setGridType] = useState(getGridType());
  useEffect(() => {
    setGridType(getGridType());
    setAnchorEl(imgElement);
  }, [imgElement]);

  function convertToGrid(gridType: 'grid-2' | 'grid-3') {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const parent: HTMLElement | null = imgElement.closest('p');
    if (!parent) return;

    const blot = Quill.find(parent);
    const index = blot.offset(quill.scroll);
    if (index === null) return;

    const elements: HTMLElement[] = [];

    let current = parent;
    while (current && current.tagName === 'P') {
      if (isEmptyParagraph(current)) break;
      const children = Array.from(current.children);

      children.forEach(child => {
        elements.push(child.cloneNode(true) as HTMLElement);
      });

      const next = current.nextElementSibling;
      current.remove();
      current = next as HTMLElement;
    }

    // Insert generic grid with collected elements
    quill.insertEmbed(index, 'gridBlot', { elements, gridType }, Quill.sources.USER);
    quill.update(Quill.sources.USER);
  }

  function convertToSingle() {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    const parent = imgElement.closest('p');

    if (parent && parent.classList.contains('ql-grid')) {
      const elements = Array.from(parent.children);
      const index = Quill.find(parent).offset(quill.scroll);

      parent.remove();

      elements.reverse().forEach(el => {
        const wrapper = document.createElement('p');
        wrapper.appendChild(el.cloneNode(true));

        quill.insertText(index, '\n', Quill.sources.USER); // ✅ Ensure paragraph break
        quill.clipboard.dangerouslyPasteHTML(index, wrapper.outerHTML, Quill.sources.USER);
      });

      quill.update(Quill.sources.USER);
    }
  }

  function removeImage() {
    const parent = imgElement.closest('p');
    const quill = quillRef?.current?.getEditor();
    if (!parent || parent.tagName !== 'P' || !quill) return;
    const images = Array.from(parent.querySelectorAll('img'));
    if (images.length === 1) {
      parent.remove();
    } else {
      imgElement.remove();
    }
  }

  return (
    <Popper
      ref={menuRef}
      anchorEl={anchorEl}
      open={Boolean(imgElement)}
      placement="top-start"
      style={{ zIndex: 10000 }}
    >
      <Card elevation={2} padding="none">
        <div className="u-flex">
          <ToggleButtonGroup
            sx={{
              '.MuiButtonBase-root': {
                border: 'none',
              },
            }}
            size="small"
            exclusive
            value={gridType}
            onChange={(_, value) => {
              if (value === 'grid-3' || value === 'grid-2') {
                convertToGrid(value);
              } else if (value === 'single') {
                convertToSingle();
              }
              setGridType(value);
              onClose();
            }}
          >
            {isFirstChild && (
              <ToggleButton value="single" disabled={gridType === 'single'}>
                <Tooltip title="Single image" placement="top">
                  <span>
                    <ImageIcon />
                  </span>
                </Tooltip>
              </ToggleButton>
            )}
            {isFirstChild && (
              <ToggleButton value="grid-2" disabled={gridType === 'grid-2'}>
                <Tooltip title="Side by side" placement="top">
                  <span>
                    <Grid2Icon />
                  </span>
                </Tooltip>
              </ToggleButton>
            )}
          </ToggleButtonGroup>
          <Tooltip title="Remove image" placement="top">
            <span>
              <IconButton
                color="indigo"
                onClick={() => {
                  removeImage();
                  onClose();
                }}
              >
                <TrashIcon />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </Card>
    </Popper>
  );
};

export default ImageOptionsMenu;
